// frontend/src/components/QuickBooking/BookingAuthStep.tsx
// ✅ COMPLETE VERSION: Updated to use refactored hooks and fix TypeScript errors

import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, User, Phone, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLogin, useRegisterCustomer } from '@/hooks/useAuth';
import { checkEmailExists } from '@/lib/api/authService';

interface BookingAuthStepProps {
  onSuccess: () => void;
  onError: (error: string) => void;
}

const BookingAuthStep: React.FC<BookingAuthStepProps> = ({ onSuccess, onError }) => {
  const [authMode, setAuthMode] = useState<'check' | 'login' | 'register'>('check');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    confirmPassword: '',
    termsAccepted: false
  });

  // ✅ FIXED: Use AuthContext with proper method name
  const { login: authLogin } = useAuth();
  
  // ✅ FIXED: Use refactored hooks
  const loginMutation = useLogin();
  const registerMutation = useRegisterCustomer();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleEmailCheck = async () => {
    console.log('handleEmailCheck called with email:', formData.email);
    
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    try {
      console.log('Calling checkEmailExists...');
      // ✅ FIXED: Use the checkEmailExists function and handle gracefully
      const result = await checkEmailExists(formData.email);
      console.log('Email check result:', result);
      
      if (result && result.exists) {
        console.log('Email exists, switching to login mode');
        setAuthMode('login');
        // toast.info('Account found! Please enter your password.');
      } else {
        console.log('Email does not exist, switching to register mode');
        setAuthMode('register');
        // toast.info('Let\'s create your account.');
      }
    } catch (err: any) {
      console.warn('Email check failed, defaulting to registration mode:', err);
      // Graceful fallback - if email check fails, go to registration
      setAuthMode('register');
      // toast.info('Let\'s create your account.');
    }
  };

  const handleLogin = async () => {
    if (!formData.email || !formData.password) {
      setError('Email and password are required');
      return;
    }

    setError('');

    try {
      // ✅ FIXED: Use the hook correctly and handle response properly
      const result = await loginMutation.mutateAsync({
        email: formData.email,
        password: formData.password,
        remember_me: false
      });

      // ✅ FIXED: The hook automatically handles token storage and context updates
      toast.success('Successfully signed in!');
      onSuccess();

    } catch (err: any) {
      const errorMessage = err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      onError(errorMessage);
      toast.error('Login failed');
    }
  };

  const handleRegister = async () => {
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.phoneNumber) {
      setError('All fields are required');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!formData.termsAccepted) {
      setError('You must accept the terms and conditions');
      return;
    }

    setError('');

    try {
      // ✅ FIXED: Use the hook correctly and handle response properly
      const result = await registerMutation.mutateAsync({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
        password_confirm: formData.confirmPassword,
        phone: formData.phoneNumber,
        terms_accepted: formData.termsAccepted,
        marketing_consent: false
      });

      // ✅ FIXED: The hook automatically handles token storage and context updates
      toast.success('Account created successfully!');
      onSuccess();

    } catch (err: any) {
      const errorMessage = err.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      onError(errorMessage);
      toast.error('Registration failed');
    }
  };

  // Email check form - matches reference design
  if (authMode === 'check') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <div className="w-4 h-5 border-2 border-blue-500 rounded-sm border-b-4"></div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Almost there!
          </h1>
          <p className="text-gray-600">
            Let's get you set up for booking
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Email Input */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your email"
                autoComplete="email"
              />
            </div>
          </div>

          <button
            onClick={handleEmailCheck}
            disabled={!formData.email || loginMutation.isPending}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium py-3 px-6 rounded-xl hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
          >
            {loginMutation.isPending ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Checking...</span>
              </>
            ) : (
              <>
                <span>Continue</span>
                <span>→</span>
              </>
            )}
          </button>
        </div>

        {/* Why create account info */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <button className="text-blue-600 text-sm font-medium hover:text-blue-700 transition-colors flex items-center space-x-2">
            <div className="w-4 h-4 border border-blue-600 rounded-full flex items-center justify-center">
              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
            </div>
            <span>Why create an account?</span>
          </button>
        </div>
      </div>
    );
  }

  // Login form - matches "Welcome back!" reference design
  if (authMode === 'login') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <div className="w-4 h-5 border-2 border-blue-500 rounded-sm border-b-4"></div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome back!
          </h1>
          <p className="text-gray-600">
            Please sign in to continue
          </p>
        </div>

        {/* Success Message */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
            <p className="text-sm text-green-700 font-medium">
              Welcome back! Please enter your password to continue
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* Email - Disabled/Pre-filled */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              disabled
              className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl text-gray-600"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="••••••••••••"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Buttons */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              onClick={() => setAuthMode('check')}
              className="py-3 px-6 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
            >
              <span>←</span>
              <span>Back</span>
            </button>
            <button
              onClick={handleLogin}
              disabled={!formData.password || loginMutation.isPending}
              className="py-3 px-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Signing In...</span>
                </>
              ) : (
                <>
                  <span>Sign In & Continue</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <button className="text-blue-600 text-sm font-medium hover:text-blue-700 transition-colors flex items-center space-x-2">
            <div className="w-4 h-4 border border-blue-600 rounded-full flex items-center justify-center">
              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
            </div>
            <span>Why create an account?</span>
          </button>
          <div className="mt-3 text-xs text-gray-500">
            • Track your booking status in real-time
          </div>
        </div>
      </div>
    );
  }

  // Registration form - matches "Create Account" reference design
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
            <div className="w-4 h-5 border-2 border-blue-500 rounded-sm border-b-4"></div>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Create Account
        </h1>
        <p className="text-gray-600">
          Just a few details to get started
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="First name"
                autoComplete="given-name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Last name"
                autoComplete="family-name"
              />
            </div>
          </div>
        </div>

        {/* Email - Pre-filled and disabled */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            value={formData.email}
            disabled
            className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl text-gray-600"
          />
        </div>

        {/* Phone Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Enter your phone number"
              autoComplete="tel"
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Create a password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-gray-400" />
              ) : (
                <Eye className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Password must be at least 6 characters long
          </p>
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Confirm your password"
              autoComplete="new-password"
            />
          </div>
        </div>

        {/* Terms and Conditions */}
        <div className="flex items-start space-x-3 pt-2">
          <input
            type="checkbox"
            id="termsAccepted"
            name="termsAccepted"
            checked={formData.termsAccepted}
            onChange={handleInputChange}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="termsAccepted" className="text-sm text-gray-700 leading-relaxed">
            I agree to the{' '}
            <a href="/terms" className="text-blue-600 hover:text-blue-700 font-medium" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-blue-600 hover:text-blue-700 font-medium" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </label>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-2 gap-3 pt-4">
          <button
            onClick={() => setAuthMode('check')}
            className="py-3 px-6 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
          >
            <span>←</span>
            <span>Back</span>
          </button>
          <button
            onClick={handleRegister}
            disabled={registerMutation.isPending}
            className="py-3 px-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
          >
            {registerMutation.isPending ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Creating Account...</span>
              </>
            ) : (
              <>
                <span>Create Account & Continue</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 pt-6 border-t border-gray-100">
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <button
              onClick={() => setAuthMode('login')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Sign in instead
            </button>
          </p>
        </div>
        <div className="mt-3 text-xs text-gray-500">
          • Track your booking status in real-time
        </div>
      </div>
    </div>
  );
};

export default BookingAuthStep;